<script setup>
import { keyBy } from 'lodash-es'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useUserPurchaseStatus } from '~/composables/useUserPurchaseStatus'

const tech = useTech()
const now = new Date()
const config = useRuntimeConfig()
const route = useRoute()

const bannerDismissed = useCookie('is-bb-banner-dismissed')

const { hasPurchased, isGuest } = useUserPurchaseStatus()

const { data } = await useFetch(config.public.BANNER_JSON_URL, {
  query: { affiliate: 'certificates_js' },
  getCachedData (key) {
    return useNuxtData(key).data.value
  },
  timeout: 5000,
  onResponseError () {
    return { data: [] }
  }
})

const user = useUser()
const { list: certifications } = useCertifications()

const hasPurchasedExamOnly = computed(() => {
  return !certifications?.some(certification => user.accessTo(certification.id, 'TRAINING'))
})

const hasPurchasedJavascriptJuniorBundlesOnly = computed(() => {
  const certItems = keyBy(certifications, 'slug')

  const juniorAccess = user.accessTo(certItems['javascript-junior-developer']?.id, 'TRAINING')
  const isJuniorAccessValid = juniorAccess === 'PURCHASED' || juniorAccess === 'REDEEMED'

  return isJuniorAccessValid &&
    Object.values(certItems).every(cert =>
      cert.slug === 'javascript-junior-developer' ||
      (user.accessTo(cert.id, 'TRAINING') !== 'PURCHASED' &&
       user.accessTo(cert.id, 'TRAINING') !== 'REDEEMED')
    )
})

const phase = data.value?.flat()
  .map((item) => ({
    ...item,
    dateString: item.ends,
    remaining: new Date(item.ends) - now,
    header: stylize(item.header),
    title: stylize(item.title),
    prelude: stylize(item.prelude),
    subtitle: stylize(item.subtitle)
  }))
  .find(item => item.remaining > 0)

function stylize (string) {
  return string?.replace('[', '<span class="text-js-yellow dark:text-still-yellow">').replace(']', '</span>')
}

const showBanner = useCookie('banner-javascript',
  {
    default: () => true,
    watch: 'shallow',
    maxAge: 60 * 60 * 24 * 7,
    SameSite: 'none'
  }
)

const showBannerOnLanding = computed(() => {
  if (route?.path?.includes('/javascript') && !route?.path?.includes('/blog')) {
    return true
  } else {
    return phase.showOnHomepage || false
  }
})

const currentTheme = computed(() => {
  return phase?.theme || 1
})

const allowBannerOnPage = inject('allowBanners', true)
</script>

<template>
  <BannerRepublic
    v-if="!isGuest && hasPurchased && tech.id === 'javascript' && !bannerDismissed && allowBannerOnPage"
    dismissible
    @dismiss="bannerDismissed = true" />
  <div
    v-show="showBanner && (isGuest || !hasPurchased || hasPurchasedExamOnly || hasPurchasedJavascriptJuniorBundlesOnly) && phase && showBannerOnLanding && allowBannerOnPage"
    class="group relative flex h-12 w-full items-center justify-center gap-3 bg-still-yellow/20 px-2 backdrop-blur-2xl md:gap-5 md:px-6 xl:gap-10"
    :class="{'!h-[50px] md:!h-12' : currentTheme === 3, '!bg-[ linear-gradient(0deg, rgba(247, 223, 30, 0.20) 0%, rgba(247, 223, 30, 0.20) 100%), #0B0C15] !h-14 md:!h-14': currentTheme === 1}">
    <NuxtLink
      :to="phase?.link ? phase?.link : '/javascript#pricing'"
      :target="route?.path === '/' || phase?.external ? '_blank' : '_self'"
      class="w-full">
      <div
        v-if="currentTheme === 'bf'"
        class="flex items-center justify-center gap-2 md:w-auto md:gap-5 xl:gap-20">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-4">
          <img
            :src="
              phase?.icon
                ? `/images/banner-${phase?.icon}.svg`
                : '/images/banner-winter.svg'
            "
            class="hidden h-4 w-auto md:h-6"
            alt="logo">
          <div
            class=" hidden !text-center text-[12px] font-black  !leading-none text-white md:inline md:w-[120px]  md:text-base xl:w-auto [&>span]:text-still-yellow"
            v-html="phase?.prelude" />
        </div>

        <div class="flex items-center gap-3 md:gap-5 xl:gap-20">
          <div class="flex w-auto flex-col items-center gap-0.5 md:w-auto xl:flex-row xl:gap-1">
            <div
              v-if="phase?.prelude"
              class="text-center text-[11px] font-black leading-none text-white md:hidden [&>span]:text-still-yellow"
              v-html="phase?.prelude" />
            <div
              v-if="phase?.title"
              class="text-center text-[11px] font-normal !leading-none text-white md:whitespace-nowrap md:text-base xl:text-lg [&>b]:font-bold [&>span]:font-bold [&>span]:text-still-yellow"
              v-html="phase?.title" />
            <div
              v-if="phase?.subtitle"
              class="text-center text-[8px] font-normal !leading-none text-white md:text-sm xl:inline xl:text-base"
              v-html="phase?.subtitle" />
          </div>

          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center xl:flex-row xl:gap-4">
            <span
              class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-xs md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white p-0 md:px-2 md:py-1 bg-transparent md:bg-still-yellow/20" />
          </div>

          <div
            v-if="phase?.addendum"
            class="w-10 text-center text-[8px] font-bold uppercase text-white md:w-auto md:px-4 md:py-1 xl:text-xs"
            style="letter-spacing: 1.2px">
            {{ phase?.addendum }}
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'autumn-sales'"
        class="my-auto w-full">
        <div class="mx-auto flex max-w-7xl -translate-x-3 items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
          <div class="flex items-center gap-2">
            <InlineSvg
              src="/images/autumn-leaf.svg"
              class="size-8 text-still-yellow"
              alt="Autumn Sales Banner" />
            <p
              class="w-18 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-still-yellow"
              v-html="phase?.prelude" />
          </div>
          <div class="[&>p]:!leading-1 flex flex-col items-center md:flex-row md:gap-10 lg:gap-16 xl:gap-20">
            <p
              class="text-sm font-normal text-white md:text-lg"
              v-html="phase?.title" />

            <p
              class="text-xs font-bold uppercase tracking-[1.8px] text-still-yellow md:text-lg"
              v-html="phase?.interlude" />
          </div>

          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center md:flex-row md:gap-2">
            <span
              v-if="phase?.id?.includes('EXT')"
              class="whitespace-nowrap text-[8px]  font-bold uppercase text-still-yellow md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-[10px] md:tracking-widest">
              ends in
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-still-yellow/20"
              box-style="" />
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 1"
        class="flex items-center justify-center gap-2 md:gap-8 xl:gap-10">
        <div
          class="hidden items-center gap-2 md:flex md:gap-4">
          <InlineSvg
            src="/images/icon-fire.svg"
            class="h-4 w-auto text-still-yellow md:h-5"
            alt="fire" />
          <div
            class="w-12 text-center text-xs !font-bold text-still-yellow md:block md:w-auto md:text-sm xl:text-base"
            v-html="phase?.prelude" />
        </div>
        <div class="flex flex-row items-center gap-1 md:flex-row md:gap-5">
          <div class="flex flex-col flex-wrap items-center justify-center gap-x-1 md:w-auto md:flex-nowrap lg:flex-row lg:gap-8 [&>span]:text-still-yellow">
            <div
              class="hidden text-[10px] font-bold text-still-yellow"
              v-html="phase?.prelude" />
            <div
              v-if="phase?.header"
              class=" items-center gap-4 text-center text-[10px] font-semibold  text-white md:w-auto md:whitespace-nowrap md:text-sm xl:text-base [&>span>span]:text-still-yellow">
              <span v-html="phase.header" />
            </div>
            <div
              v-if="phase?.title"
              class="items-center gap-4 text-center text-[10px] font-semibold text-white md:w-auto md:whitespace-nowrap md:text-sm md:!leading-[1.2rem] xl:text-base [&>span>span]:text-still-yellow">
              <span v-html="phase.title" />
            </div>
            <NuxtImg
              src="/images/banner-alain.png"
              class="ml-5 hidden h-[36px] rounded-full" />
            <div
              v-if="phase?.subtitle"
              class="text-center text-[10px] font-medium text-white md:text-xs xl:text-base min-[1600px]:block [&>b]:font-bold [&>b]:text-still-yellow [&>span]:!font-semibold [&>span]:text-still-yellow"
              v-html="phase?.subtitle" />
            <div
              v-if="phase?.interlude"
              class="block text-center text-[10px] font-bold text-white md:text-base md:leading-none lg:hidden xl:text-base [&>span]:font-bold [&>span]:text-still-yellow"
              v-html="phase?.interlude" />
          </div>
          <div
            v-if="phase?.interlude"
            class="hidden text-center text-[10px] font-bold text-white md:text-base md:leading-none lg:block xl:text-base [&>span]:font-bold [&>span]:text-still-yellow lg:[&>span]:ml-2"
            v-html="phase?.interlude" />
          <div class="flex flex-col items-center gap-0 md:flex-row md:gap-2 lg:gap-0">
            <p
              v-if="phase?.preAddendum"
              class="rounded-md text-center text-[8px] font-bold uppercase text-white md:text-[10px] lg:bg-still-yellow/20 lg:px-4 lg:py-1">
              {{ phase?.preAddendum }}
            </p>
            <div
              v-if="phase?.addendum"
              class="w-12 text-center text-[8px] font-bold text-white lg:w-auto  lg:px-4 lg:py-1 lg:text-xs lg:[&>span]:ml-2"
              v-html="phase?.addendum" />
          </div>
          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center md:flex-row md:gap-4">
            <span
              v-if="phase?.id?.includes('EXT')"
              class="whitespace-nowrap text-[8px] font-bold uppercase text-still-yellow md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-[9px] md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{ remaining: phase?.remaining, daysToHours: phase?.showDaysAsHours }"
              class="dark"
              box-class="text-white lg:scale-[1.1] lg:translate-x-2 md:px-2 md:py-1 md:bg-still-yellow/30" />
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'FW-LIVE'"
        class="mx-auto max-w-screen-2xl text-eerie-black  dark:text-white">
        <div
          class="flex w-full items-center justify-center">
          <!-- Title section - changes layout based on screen size -->
          <div class="w-[78px] p-2 sm:w-[124px] sm:p-2 md:p-0  md:pl-5 lg:w-[154px] lg:pl-6 xl:w-auto">
            <div
              class="text-xs font-bold  uppercase  !leading-none  sm:text-xs md:items-center  md:text-sm lg:text-base [&>span]:text-js-yellow dark:[&>span]:text-still-yellow "
              v-html="phase?.prelude" />
          </div>

          <!-- Content section -->
          <div class="px-2 py-1 md:px-5 md:py-3 lg:px-6">
            <div class="flex w-full flex-row gap-4 md:flex-row md:items-center md:justify-between">
              <!-- Training info -->
              <div
                v-show="phase?.title||phase?.subtitle"
                class="flex flex-col items-center text-center leading-none  md:mx-3 md:text-sm lg:mx-4 lg:flex-row lg:gap-10">
                <p
                  class="text-[10px] font-bold text-js-yellow sm:text-sm lg:whitespace-nowrap lg:text-base dark:text-still-yellow"
                  v-html="phase?.title" />
                <p
                  class="text-[10px] font-normal text-eerie-black sm:whitespace-nowrap  sm:text-sm lg:text-base dark:text-white"
                  v-html="phase?.subtitle" />
              </div>

              <!-- Date and Button -->
              <div class="mt-1 flex items-center justify-between gap-4 md:mt-0">
                <!-- Date repositioning -->
                <span class="order-2 text-xs font-semibold md:order-1 md:mr-3 md:text-sm lg:mr-4 lg:text-base">{{ phase?.addendum }}</span>

                <!-- Reserve button - hidden on smallest screens -->
                <button class="order-1 hidden items-center whitespace-nowrap rounded bg-js-yellow px-2 py-1.5 text-xs font-semibold text-black hover:bg-white sm:flex md:order-2 md:px-3 md:text-sm dark:bg-still-yellow">
                  {{ phase?.cta }}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="ml-1 size-3 md:size-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center md:flex-row md:gap-4">
            <span
              v-if="phase?.isExtended"
              class="whitespace-nowrap text-[8px] font-bold uppercase text-still-yellow md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-[9px] md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{ remaining: phase?.remaining, daysToHours: phase?.showDaysAsHours }"
              class="dark"
              box-class="dark:text-white lg:scale-[1.2] lg:translate-x-2 md:px-2 md:py-1 md:bg-still-yellow/30" />
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'autumn-sales'"
        class="my-auto w-full">
        <div class="mx-auto flex max-w-7xl items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
          <div class="flex items-center gap-2">
            <InlineSvg
              src="/images/autumn-leaf.svg"
              class="size-6 text-still-yellow md:size-8"
              alt="Autumn Sales Banner" />
            <p
              class="w-18 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-still-yellow"
              v-html="phase?.prelude" />
          </div>
          <div class="[&>p]:!leading-1 flex w-32 flex-col items-center md:w-auto md:flex-row md:gap-10 lg:gap-16 xl:gap-20">
            <p
              class="text-center text-sm font-normal text-white
               md:text-lg"
              v-html="phase?.title" />

            <p
              v-if="phase?.interlude"
              class="text-xs font-bold uppercase tracking-[1.8px] text-still-yellow md:text-lg"
              v-html="phase?.interlude" />
          </div>

          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center md:flex-row md:gap-2">
            <span
              v-if="phase?.id?.includes('EXTENDED')"
              class="whitespace-nowrap text-[8px]  font-bold uppercase text-still-yellow md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-[10px] md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-still-yellow md:text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-still-yellow/20"
              box-style="" />
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'halloween-sale'"
        class="my-auto w-full">
        <div class="mx-auto flex max-w-7xl items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
          <div class="flex items-center gap-2">
            <InlineSvg
              src="/images/pumpkin.svg"
              class="hidden size-6 sm:block  md:size-6"
              alt="Halloween Sales Banner" />
            <p
              class="w-24 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-[#FF6666]"
              v-html="phase?.prelude" />
          </div>
          <div class="flex w-32 flex-col items-center md:w-auto md:flex-row md:gap-10 lg:gap-16 xl:gap-20 [&>p]:!leading-[90%]">
            <p
              class="text-center text-sm font-normal text-white md:text-lg"
              v-html="phase?.title" />

            <p
              v-if="phase?.interlude"
              class="text-xs font-bold uppercase tracking-[1.8px] text-still-yellow md:text-lg"
              v-html="phase?.interlude" />
          </div>

          <div
            v-if="phase?.showCountdown"
            class="mt-1 flex flex-col items-center md:mt-0 md:flex-row md:gap-2">
            <span
              v-if="phase?.id?.includes('EXTENDED')"
              class="whitespace-nowrap text-[8px]  font-bold uppercase text-still-yellow md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="-mb-1 whitespace-nowrap text-[8px] font-bold uppercase text-white sm:mb-0 md:text-[10px] md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-[#FF6666] md:text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-[#FF6666]/20"
              box-style="" />
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'FW-OFFER'"
        class="mx-auto max-w-screen-2xl text-eerie-black  dark:text-white">
        <div
          class="flex w-full items-center justify-center">
          <!-- Title section - changes layout based on screen size -->
          <div class="w-[78px] p-2 sm:w-[124px] sm:p-2 md:p-0  md:pl-5 lg:w-[154px] lg:pl-6 xl:w-auto">
            <div
              class="text-xs font-bold  uppercase  !leading-none  sm:text-xs md:items-center  md:text-sm lg:text-xl [&>span]:text-js-yellow dark:[&>span]:text-still-yellow "
              v-html="phase?.prelude" />
          </div>

          <!-- Content section -->
          <div class="px-2 py-1 md:px-5 md:py-3 lg:px-6">
            <div class="flex w-full flex-row gap-4 md:flex-row md:items-center md:justify-between">
              <!-- Training info -->
              <div
                v-show="phase?.title||phase?.subtitle"
                class="flex flex-col items-center text-center leading-none  md:mx-3 md:text-sm lg:mx-4 lg:flex-row lg:gap-10">
                <p
                  class="text-[10px] font-bold text-js-yellow sm:text-sm lg:whitespace-nowrap lg:text-base dark:text-still-yellow"
                  v-html="phase?.title" />
                <p
                  class="max-w-48 text-[10px] font-normal text-eerie-black sm:max-w-full sm:text-sm  md:whitespace-nowrap lg:text-base dark:text-white"
                  v-html="phase?.subtitle" />
              </div>

              <!-- Date and Button -->
              <div class="mt-1 flex items-center justify-between gap-4 md:mt-0">
                <!-- Date repositioning -->
                <span class="order-2 text-xs font-semibold md:order-1 md:mr-3 md:text-sm lg:mr-4 lg:text-base">{{ phase?.addendum }}</span>

                <!-- Reserve button - hidden on smallest screens -->
                <button
                  v-if="phase?.cta"
                  class="order-1 hidden items-center whitespace-nowrap rounded bg-js-yellow px-2 py-1.5 text-xs font-semibold text-black hover:bg-white sm:flex md:order-2 md:px-3 md:text-sm dark:bg-still-yellow">
                  {{ phase?.cta }}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="ml-1 size-3 md:size-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center md:flex-row md:gap-4">
            <span
              v-if="phase?.isExtended"
              class="whitespace-nowrap text-[8px] font-bold uppercase text-still-yellow md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-[9px] md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{ remaining: phase?.remaining, daysToHours: phase?.showDaysAsHours }"
              class="dark"
              box-class="dark:text-white lg:scale-[1.2] lg:translate-x-2 md:px-2 md:py-1 md:bg-still-yellow/30" />
          </div>
        </div>
      </div>
    </NuxtLink>
    <button
      class="absolute inset-y-0 my-auto size-5 shrink-0 max-[500px]:relative min-[500px]:right-2 xl:right-3"
      @click="showBanner = false">
      <span class="sr-only">close</span><XMarkIcon class="x-5 h-5 text-white" />
    </button>
  </div>
</template>
